/**
 * Sawyer 10 customer-booking-verify-policy (C5 C6) — the owner's attestations
 * on the row: "Owner verified care · Oct 24, 3:12 PM" (C5, the owner ticked
 * "I have verified the feeding notes, medications and allergies are up to
 * date" when booking) and "Med policy agreed · Oct 24" (C6).
 *
 * Renders ONLY on a real timestamp. `null` means the facility did not ask (a
 * desk booking, a service not ticked, the setting off) and `undefined` means
 * an older server; neither may render (Principle 5). Not the #716 staff
 * check-in "verified" stamp, and never worded like it.
 *
 * Non-interactive text: words carry the meaning, the title is a nicety. Times
 * read in `timeZone` when given, else the device's zone (desk devices sit in
 * the facility).
 */
import './OwnerCareCheckPill.css';

export interface OwnerCareCheckPillProps {
  careAttestedAt: string | null | undefined;
  medicationPolicyAckAt: string | null | undefined;
  timeZone?: string;
  testId?: string;
}

function parse(iso: string | null | undefined): Date | null {
  if (typeof iso !== 'string' || iso.length === 0) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatCareAttestedAt(d: Date, timeZone?: string): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    ...(timeZone ? { timeZone } : {}),
  }).format(d);
}

export function formatPolicyAckAt(d: Date, timeZone?: string): string {
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', ...(timeZone ? { timeZone } : {}) }).format(d);
}

export function OwnerCareCheckPill({
  careAttestedAt,
  medicationPolicyAckAt,
  timeZone,
  testId,
}: OwnerCareCheckPillProps): JSX.Element | null {
  const care = parse(careAttestedAt);
  const ack = parse(medicationPolicyAckAt);
  if (!care && !ack) return null;
  return (
    <span className="ferro-owner-care-pills" data-testid={testId}>
      {care ? (
        <span
          className="ferro-owner-care-pill"
          data-testid={testId ? `${testId}-care` : undefined}
          title="The owner confirmed feeding, medications and allergies when booking"
        >
          Owner verified care · {formatCareAttestedAt(care, timeZone)}
        </span>
      ) : null}
      {ack ? (
        <span
          className="ferro-owner-care-pill"
          data-testid={testId ? `${testId}-policy` : undefined}
          title="The owner agreed to the medication policy when booking"
        >
          Med policy agreed · {formatPolicyAckAt(ack, timeZone)}
        </span>
      ) : null}
    </span>
  );
}
